import { Badge, Card, CardTitle, EmptyState, Notice, Skeleton, Spot } from "@custos/ui";
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router";
import { PageHeader } from "../components/AdminShell";
import { PauseControl, SignerCard } from "../components/controls";
import { errorMessage } from "../lib/errors";
import { useAdminMe, useOverview } from "../lib/queries";

function Stat({
  label,
  value,
  to,
  tone,
}: {
  label: string;
  value: number | undefined;
  to?: string;
  tone?: "warning";
}) {
  const body = (
    <div className="flex h-full flex-col justify-between gap-2 rounded-card bg-surface p-5 transition-colors duration-200 hover:bg-hover">
      <p className="text-sm text-muted">{label}</p>
      <p
        className={
          tone === "warning" && value
            ? "text-4xl tracking-tight text-accent"
            : "text-4xl tracking-tight text-fg"
        }
      >
        {value ?? "—"}
      </p>
    </div>
  );
  return to ? (
    <Link to={to} className="block">
      {body}
    </Link>
  ) : (
    body
  );
}

export function OverviewPage() {
  const overview = useOverview();
  const me = useAdminMe();
  const o = overview.data;

  return (
    <>
      <PageHeader title="Overview" subtitle={me.data ? `Signed in as ${me.data.email}` : undefined} />
      {overview.error && <Notice tone="danger">{errorMessage(overview.error)}</Notice>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <section
          aria-label="Withdrawals switch"
          className="bg-aurora animate-rise rounded-[32px] p-6 sm:p-8 lg:col-span-2"
        >
          <p className="text-white/80">Withdrawals</p>
          <p className="mt-1 text-5xl tracking-[-0.03em]" data-testid="withdrawals-state">
            {o ? (o.withdrawalsPaused ? "Paused" : "Running") : "—"}
          </p>
          <p className="mt-2 max-w-md text-sm text-white/80">
            The emergency switch stops all new and queued withdrawals instantly. Resuming needs a 2FA code.
          </p>
          <div className="mt-6 max-w-sm [&_[role=alert]]:bg-black/30">
            <PauseControl />
          </div>
          <p className="mt-4 text-sm text-white/80" data-testid="swaps-state">
            Swaps: {o ? (o.swapsPaused ? "PAUSED" : "running") : "—"} ·{" "}
            <Link to="/limits" className="underline">
              swap switch and limits
            </Link>
          </p>
        </section>
        <SignerCard />
      </div>

      <div className="stagger mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label="Waiting for approval"
          value={o?.pendingApproval}
          to="/withdrawals?status=PENDING_APPROVAL"
          tone="warning"
        />
        <Stat label="Open withdrawals" value={o?.openWithdrawals} to="/withdrawals" />
        <Stat label="Deposits confirming" value={o?.confirmingDeposits} to="/deposits" />
        <Stat
          label={`Users${o?.blockedUsers ? ` (${o.blockedUsers} blocked)` : ""}`}
          value={o?.users}
          to="/users"
        />
      </div>

      <Card className="mt-4">
        <CardTitle className="mb-3 flex items-center gap-2">
          <AlertTriangle className="size-5 text-warning" aria-hidden /> Open alerts
        </CardTitle>
        {overview.isPending ? (
          <Skeleton className="h-16" />
        ) : !o || o.alerts.length === 0 ? (
          <EmptyState icon={<Spot name="lock" />} title="No open alerts" />
        ) : (
          <ul className="flex flex-col divide-y divide-line" data-testid="alerts">
            {o.alerts.map((a) => (
              <li key={a.id} className="flex flex-col gap-1 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="warning">{a.type.replaceAll("_", " ").toLowerCase()}</Badge>
                  {a.chain && <Badge>{a.chain}</Badge>}
                </div>
                <p className="text-sm text-fg">{a.message}</p>
                <p className="text-xs text-muted">Last seen {new Date(a.lastSeenAt).toLocaleString()}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
